/**
 * Checklist Template Form – table-based editor with add/remove/reorder/required toggle.
 *
 * Data flows:
 *   <script id="checklist-template-items" type="application/json">  → editor →
 *   hidden input (items_json) → POST
 *
 * Translations come from `data-tr-*` attributes on the form element.
 *
 * Submit-button protection: the form carries `data-protect-submit`, picked up
 * by `frontend/src/utils/forms/submit-button.js`. No spinner code lives here.
 *
 * Item shape: { item: string, required: boolean }
 */

let _items = [];
let _translations = {};
let _container;
let _hiddenInput;
let _countEl;
let _dragSrcIndex = null;

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function syncHiddenInput() {
    if (_hiddenInput) {
        _hiddenInput.value = JSON.stringify(_items);
    }
    updateItemCount();
}

function updateItemCount() {
    if (!_countEl) return;
    const n = _items.length;
    if (n === 0) {
        _countEl.textContent = '';
    } else {
        const label = n === 1 ? _translations.itemCount : _translations.itemCountPlural;
        _countEl.textContent = `(${n} ${label || 'items'})`;
    }
}

function renderItems() {
    if (!_container) return;

    if (_items.length === 0) {
        _container.innerHTML = `
            <tr>
                <td colspan="4" class="px-3 py-6 text-center text-gray-400 dark:text-gray-500 text-sm">
                    ${escapeHtml(_translations.emptyState || 'No items yet.')}
                </td>
            </tr>`;
        syncHiddenInput();
        return;
    }

    _container.innerHTML = _items.map((entry, idx) => `
        <tr class="checklist-item border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 cursor-grab active:cursor-grabbing"
            draggable="true" data-index="${idx}">
            <td class="px-3 py-1.5 text-gray-400 dark:text-gray-500 drag-handle">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
                </svg>
            </td>
            <td class="px-3 py-1.5 text-gray-900 dark:text-white">${escapeHtml(entry.item)}</td>
            <td class="px-3 py-1.5 text-center">
                <button type="button" data-action="toggle-required" data-index="${idx}"
                        class="text-xs px-2 py-0.5 rounded cursor-pointer ${entry.required
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                            : 'bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                        }">
                    ${entry.required ? escapeHtml(_translations.required || 'Required') : escapeHtml(_translations.optional || 'Optional')}
                </button>
            </td>
            <td class="px-3 py-1.5 text-center">
                <button type="button" data-action="remove" data-index="${idx}"
                        class="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-400 cursor-pointer">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                </button>
            </td>
        </tr>
    `).join('');

    syncHiddenInput();
    attachDragListeners();
    if (typeof window.reinitFlowbiteWidgets === 'function') window.reinitFlowbiteWidgets();
}

function addItem(text) {
    const trimmed = text.trim();
    if (!trimmed) return;
    _items.push({ item: trimmed, required: true });
    renderItems();
}

function removeItem(index) {
    _items.splice(index, 1);
    renderItems();
}

function toggleRequired(index) {
    if (_items[index]) {
        _items[index].required = !_items[index].required;
        renderItems();
    }
}

function moveItem(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    if (toIndex < 0 || toIndex >= _items.length) return;
    const [moved] = _items.splice(fromIndex, 1);
    _items.splice(toIndex, 0, moved);
    renderItems();
}

function attachDragListeners() {
    const rows = _container.querySelectorAll('tr.checklist-item[draggable]');
    rows.forEach(row => {
        row.addEventListener('dragstart', function (e) {
            _dragSrcIndex = parseInt(this.dataset.index);
            this.classList.add('opacity-50');
            e.dataTransfer.effectAllowed = 'move';
        });
        row.addEventListener('dragend', function () {
            this.classList.remove('opacity-50');
            _container.querySelectorAll('tr.checklist-item').forEach(r =>
                r.classList.remove('border-t-2', 'border-primary-500')
            );
        });
        row.addEventListener('dragover', function (e) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            this.classList.add('border-t-2', 'border-primary-500');
        });
        row.addEventListener('dragleave', function () {
            this.classList.remove('border-t-2', 'border-primary-500');
        });
        row.addEventListener('drop', function (e) {
            e.preventDefault();
            this.classList.remove('border-t-2', 'border-primary-500');
            const toIndex = parseInt(this.dataset.index);
            if (_dragSrcIndex !== null && _dragSrcIndex !== toIndex) {
                moveItem(_dragSrcIndex, toIndex);
            }
            _dragSrcIndex = null;
        });
    });
}

function readItemsFromDataIsland() {
    const el = document.getElementById('checklist-template-items');
    if (!el) return [];
    try {
        const parsed = JSON.parse((el.textContent || '').trim() || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function readTranslationsFromDataset(form) {
    const ds = form.dataset || {};
    return {
        required: ds.trRequired || 'Required',
        optional: ds.trOptional || 'Optional',
        removeItem: ds.trRemoveItem || 'Remove item',
        itemCount: ds.trItemCount || 'item',
        itemCountPlural: ds.trItemCountPlural || 'items',
        emptyState: ds.trEmptyState || 'No items yet. Add your first checklist item above.',
    };
}

export function initChecklistTemplateForm() {
    const form = document.getElementById('checklist-template-form');
    if (!form) return;

    _translations = readTranslationsFromDataset(form);
    _items = readItemsFromDataIsland().map(entry => {
        if (typeof entry === 'string') return { item: entry, required: true };
        return { item: String(entry.item || ''), required: entry.required !== false };
    }).filter(e => e.item.trim());

    _container = document.getElementById('checklist-items-container');
    _hiddenInput = document.getElementById('id_items_json');
    _countEl = document.getElementById('item-count');

    const newInput = document.getElementById('new-item-input');
    const addBtn = document.getElementById('add-item-btn');
    const addRow = document.getElementById('add-item-row');

    if (addBtn) {
        addBtn.addEventListener('click', function () {
            if (addRow) {
                addRow.classList.toggle('hidden');
                if (!addRow.classList.contains('hidden') && newInput) {
                    newInput.focus();
                }
            }
        });
    }

    if (newInput) {
        newInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addItem(this.value);
                this.value = '';
                this.focus();
            }
        });
    }

    if (_container) {
        _container.addEventListener('click', function (e) {
            const btn = e.target.closest('[data-action]');
            if (!btn) return;
            const idx = parseInt(btn.dataset.index);
            if (btn.dataset.action === 'remove') removeItem(idx);
            if (btn.dataset.action === 'toggle-required') toggleRequired(idx);
        });
    }

    // Validation clearing
    form.addEventListener('input', function (e) {
        const field = e.target;
        if ((field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') && field.value.trim()) {
            const fieldGroup = field.closest('.field-group');
            if (fieldGroup) {
                fieldGroup.classList.remove('has-error');
                fieldGroup.querySelectorAll('.field-error, p.text-red-600').forEach(msg => msg.remove());
            }
        }
    });

    renderItems();
}

export function autoInitChecklistTemplateForm() {
    initChecklistTemplateForm();
}
